import classNames from 'classnames';
import { InlineTooltip } from '../InlineTooltip';
import { MaterialIcon } from '../MaterialIcon';
import { CC_CARD_TEXT } from './utils';

type CcProductCardEarnRateProps = {
  caption: string;
  /** The dominant figure, e.g. "Up to 2%" / "As low as HK$4/mile". */
  rate: string;
  /** The reward currency named after the rate. */
  currency: string;
  /** Shown only where the reward is not paid in plain cash (FR-1.4.2). */
  explainer?: { label: string; text: string };
  /** Shown only where registration is needed to earn the rate (FR-1.4.3). */
  registration?: { label: string; note: string };
  /** The minimum monthly spend the rate requires (FR-1.4.4). */
  minSpend?: string;
};

// An invisible box around each tooltip trigger, so the 16px icon and the 20px flag still take a
// 44px tap (BRD-01 §4) without moving anything around them.
const ICON_TAP_AREA = classNames('relative before:absolute before:-inset-3.5');
// The flag is wider than 44px already; the box only needs height, but an empty absolute box without
// left and right is 0px wide, so it spans the flag's own width too.
const FLAG_TAP_AREA = classNames(
  'relative before:absolute before:-inset-y-3 before:inset-x-0',
);

/** Tier 1 of the benefits block: the headline earn rate (FR-1.4.1). */
export const CcProductCardEarnRate: React.FC<CcProductCardEarnRateProps> = ({
  caption,
  rate,
  currency,
  explainer,
  registration,
  minSpend,
}) => (
  <div
    data-component="cc-product-card-earn-rate"
    className="flex flex-col gap-spacing/gap/soft-4px px-spacing/padding/x-sm py-2.5 bg-fill/alternative-1"
  >
    <div className="flex min-h-5 items-center justify-between gap-spacing/gap/soft-8px">
      <span
        className={classNames(
          CC_CARD_TEXT.label,
          'text-text/secondary-lighter',
        )}
      >
        {caption}
      </span>
      {explainer && (
        <InlineTooltip
          onOpenEvent="click"
          content={<span>{explainer.text}</span>}
          infoClassName={ICON_TAP_AREA}
          trigger={
            <MaterialIcon
              icon="info"
              px={16}
              className="align-middle text-text/secondary-lighter"
              aria-label={explainer.label}
            />
          }
        />
      )}
    </div>
    <div className="flex flex-wrap items-baseline gap-x-spacing/gap/soft-6px">
      <span className={classNames(CC_CARD_TEXT.headline, 'text-text/primary')}>
        {rate}
      </span>
      <span
        className={classNames(
          CC_CARD_TEXT.note,
          'font-font-weight/medium text-text/secondary-lighter',
        )}
      >
        {currency}
      </span>
      {registration && (
        <InlineTooltip
          onOpenEvent="click"
          content={<span>{registration.note}</span>}
          infoClassName={FLAG_TAP_AREA}
          trigger={
            <span
              className={classNames(
                CC_CARD_TEXT.badge,
                'rounded-radius/tooltip px-spacing/padding/soft-6px py-0.5 bg-highlights/red-light text-highlights/red-dark',
              )}
            >
              {registration.label}
            </span>
          }
        />
      )}
    </div>
    {minSpend && (
      <span
        className={classNames(CC_CARD_TEXT.note, 'text-text/secondary-lighter')}
      >
        {minSpend}
      </span>
    )}
  </div>
);
